import type { Database } from '@shkola/db-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type Db, must, useDb } from './db';
import { type HomeworkAnswer, OFFLINE_ACTIONS } from './offline';

export type SubmissionStatus = Database['public']['Enums']['submission_status'];

export interface HomeworkItem {
  id: string;
  courseId: string;
  subject: string;
  title: string;
  body: string | null;
  /** «за що оцінка»: one criterion per line, e.g. «2 бали — розвʼязання» */
  criteria: string | null;
  dueOn: string;
  requiresSubmission: boolean;
  lessonDate: string | null;
  createdAt: string;
}

export interface Submission {
  id: string;
  homeworkId: string;
  studentId: string;
  body: string;
  status: SubmissionStatus;
  submittedAt: string;
  feedback: string | null;
  /** photos, recordings, PDFs: paths in the `submissions` bucket */
  attachments: string[];
}

const HOMEWORK_SELECT = `
  id, course_id, title, body, criteria, due_on, requires_submission, created_at,
  lesson:lessons!homework_lesson_id_course_id_fkey(date),
  course:courses!homework_school_id_course_id_fkey(subject:subjects!courses_school_id_subject_id_fkey(name))
`;

function toHomework(h: {
  id: string;
  course_id: string;
  title: string;
  body: string | null;
  criteria: string | null;
  due_on: string;
  requires_submission: boolean;
  created_at: string;
  lesson: { date: string } | null;
  course: { subject: { name: string } };
}): HomeworkItem {
  return {
    id: h.id,
    courseId: h.course_id,
    subject: h.course.subject.name,
    title: h.title,
    body: h.body,
    criteria: h.criteria,
    dueOn: h.due_on,
    requiresSubmission: h.requires_submission,
    lessonDate: h.lesson?.date ?? null,
    createdAt: h.created_at,
  };
}

const toSubmission = (
  s: Database['public']['Tables']['homework_submissions']['Row'],
): Submission => ({
  id: s.id,
  homeworkId: s.homework_id,
  studentId: s.student_member_id,
  body: s.body,
  status: s.status,
  submittedAt: s.submitted_at,
  feedback: s.feedback,
  attachments: s.attachments,
});

export interface HomeworkToDo {
  /** not handed in and the date has passed */
  overdue: HomeworkItem[];
  /** due today or tomorrow, not handed in yet */
  soon: HomeworkItem[];
  /** everything still to hand in, by due date */
  open: HomeworkItem[];
}

/** What a student still has to hand in: an answer sent back for rework counts as not done. */
export function homeworkToDo(
  homework: ReadonlyArray<HomeworkItem>,
  submissions: ReadonlyArray<Submission>,
  today: string,
  tomorrow: string,
): HomeworkToDo {
  const done = new Set(submissions.filter((s) => s.status !== 'returned').map((s) => s.homeworkId));
  const open = homework
    .filter((h) => h.requiresSubmission && !done.has(h.id))
    .sort((a, b) => a.dueOn.localeCompare(b.dueOn));
  return {
    overdue: open.filter((h) => h.dueOn < today),
    soon: open.filter((h) => h.dueOn === today || h.dueOn === tomorrow),
    open: open.filter((h) => h.dueOn >= today),
  };
}

/** A student's homework from a date on, with their own answers. */
export function useStudentHomework(studentId: string, from: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['homework', 'student', studentId, from],
    queryFn: async () => {
      const courses = must(
        await db.from('course_students').select('course_id').eq('student_member_id', studentId),
      ).map((r) => r.course_id);
      const [homework, submissions] = await Promise.all([
        db
          .from('homework')
          .select(HOMEWORK_SELECT)
          .in('course_id', courses)
          .gte('due_on', from)
          .order('due_on'),
        db.from('homework_submissions').select('*').eq('student_member_id', studentId),
      ]);
      return {
        homework: must(homework).map(toHomework),
        submissions: must(submissions).map(toSubmission),
      };
    },
  });
}

/** A course's homework with every student's answer (teacher view). */
export function useCourseHomework(courseId: string | undefined) {
  const db = useDb();
  return useQuery({
    queryKey: ['homework', 'course', courseId],
    enabled: courseId !== undefined,
    queryFn: async () => {
      const [homework, submissions] = await Promise.all([
        db
          .from('homework')
          .select(HOMEWORK_SELECT)
          .eq('course_id', courseId as string)
          .order('due_on', { ascending: false }),
        db
          .from('homework_submissions')
          .select('*')
          .eq('course_id', courseId as string),
      ]);
      return {
        homework: must(homework).map(toHomework),
        submissions: must(submissions).map(toSubmission),
      };
    },
  });
}

export function useHomeworkActions() {
  const db = useDb();
  const client = useQueryClient();
  const invalidate = () => client.invalidateQueries({ queryKey: ['homework'] });
  return {
    create: useMutation({
      mutationFn: async (a: {
        schoolId: string;
        courseId: string;
        lessonId?: string;
        title: string;
        body?: string;
        criteria?: string;
        dueOn: string;
        requiresSubmission: boolean;
      }) =>
        must(
          await db.from('homework').insert({
            school_id: a.schoolId,
            course_id: a.courseId,
            lesson_id: a.lessonId ?? null,
            title: a.title,
            body: a.body || null,
            criteria: a.criteria?.trim() || null,
            due_on: a.dueOn,
            requires_submission: a.requiresSubmission,
          }),
        ),
      onSettled: invalidate,
    }),
    /**
     * Hand in (or hand in again). Works without connection: the answer waits in the queue and
     * goes out later; its client-made id keeps a resend from creating a second answer.
     */
    submit: useMutation<unknown, Error, HomeworkAnswer>({
      mutationKey: OFFLINE_ACTIONS.submitHomework,
    }),
    review: useMutation({
      mutationFn: async (a: { id: string; status: 'accepted' | 'returned'; feedback: string }) =>
        must(
          await db
            .from('homework_submissions')
            .update({ status: a.status, feedback: a.feedback || null })
            .eq('id', a.id),
        ),
      onSettled: invalidate,
    }),
  };
}

export async function fetchCourseStudents(db: Db, courseId: string) {
  return must(
    await db
      .from('course_students')
      .select(
        'student:school_members!course_students_school_id_student_member_id_fkey(id, first_name, last_name)',
      )
      .eq('course_id', courseId),
  )
    .map((r) => r.student)
    .sort((a, b) => a.last_name.localeCompare(b.last_name, 'uk'));
}

/** What kind of file an answer carries, by its name. */
export const attachmentKind = (path: string): 'image' | 'audio' | 'pdf' =>
  /\.(webm|m4a|mp4|mp3|ogg|wav)$/i.test(path) ? 'audio' : /\.pdf$/i.test(path) ? 'pdf' : 'image';

/** A random name for a file (the phone has no `crypto.randomUUID`). */
const fileId = () =>
  globalThis.crypto?.randomUUID?.() ??
  Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');

/** Uploads one file of an answer into the student's own folder; returns its path. Needs a connection. */
export function useSubmissionUpload() {
  const db = useDb();
  return useMutation({
    mutationFn: async (a: {
      schoolId: string;
      homeworkId: string;
      studentId: string;
      /** a Blob in the browser; the bytes of a file on the phone */
      file: Blob | ArrayBuffer;
      extension: string;
      contentType?: string;
    }) => {
      const path = `${a.schoolId}/${a.homeworkId}/${a.studentId}/${fileId()}.${a.extension}`;
      const contentType = a.contentType ?? (a.file instanceof Blob ? a.file.type : undefined);
      const { error } = await db.storage
        .from('submissions')
        .upload(path, a.file, { contentType: contentType || undefined, upsert: false });
      if (error) throw error;
      return path;
    },
  });
}

/** Short-lived links to an answer's files (the bucket is private). */
export function useAttachmentLinks(paths: ReadonlyArray<string>) {
  const db = useDb();
  return useQuery({
    queryKey: ['submission-files', [...paths].sort().join('|')],
    enabled: paths.length > 0,
    staleTime: 30 * 60_000,
    queryFn: async () =>
      Object.fromEntries(
        must(await db.storage.from('submissions').createSignedUrls([...paths], 3600)).map((f) => [
          f.path ?? '',
          f.signedUrl,
        ]),
      ) as Record<string, string>,
  });
}
